import type { models } from "jiffy-api";
import { atom } from "jotai";

export type PinningSessionData = {
  sessionId: string;
  state: models.designLab.pinning.SessionState;
  summary: string | null;
};

export type CurrentPinningSession = {
  artworkId: string;
  sessionId: string;
};

export type PinState = "draft" | "processing" | "ready" | "needs_clarification" | "unsupported";

// bbox2d format: [x1, y1, x2, y2] where (x1,y1) is top-left and (x2,y2) is bottom-right
export type BoundingBox2D = {
  bbox2d: [number, number, number, number];
};

export type MaskObject = {
  maskUrl: string;
  numObjectsDetected: number;
  inputSize: [number, number]; // [width, height]
  maskSize: [number, number]; // [width, height]
};

export type AreaOfInterest = {
  type: "bounding_box" | "mask";
  objects: Array<BoundingBox2D | MaskObject>;
};
export type ClarificationOption = {
  label: string;
  value: string;
};

export type ClarificationStatus = "resolved" | "pending" | "asking";

export type Clarification = {
  type: string; // "color_shade", "font_selection", "font_color", "scope", etc.
  question: string;
  label?: string; // Optional label for submission format
  options: ClarificationOption[];
  status: ClarificationStatus;
  answer: string | null;
};

export type TextInfo = {
  value: string | null;
  fontFamily: string;
  color: string;
};

export type PinMessage = {
  id: string;
  role: "user" | "ai";
  content: string;
  timestamp: number;
  clarifications?: Clarification[]; // For multiple clarifications
  textInfo?: TextInfo; // Text info for font/color preview rendering
};

export type Pin = {
  id: string;
  coordinates: { x: number; y: number }; // Natural coordinates
  state: PinState;
  messages: PinMessage[];
  instruction?: string;
  areasOfInterest?: AreaOfInterest;
  segmentationMaskUrl?: string; // Internal URL for persisted mask (overrides maskUrl in areasOfInterest)
  highlightAreasEnabled?: boolean;
  thinkingText?: string;
  isNew?: boolean;
};

export const pinningModeAtom = atom<boolean>(false);

// Stores all pinning sessions (active + completed) keyed by artworkId
export const pinningSessionsAtom = atom<Record<string, PinningSessionData>>({});

// Derived atom: true if any session is active or completed (design services requested)
export const hasRequestedDesignServicesAtom = atom(get => {
  const sessions = get(pinningSessionsAtom);
  return Object.values(sessions).some(
    session => session.state === "active" || session.state === "completed"
  );
});

// The session currently open in pinning UI
export const currentPinningSessionAtom = atom<CurrentPinningSession | null>(null);

export const pinsAtom = atom<Pin[]>([]);
export const activePinIdAtom = atom<string | null>(null);
export const hoveredPinIdAtom = atom<string | null>(null);

// Controls visibility of pin markers on the canvas
export const showPinsAtom = atom<boolean>(true);

// Tracks where the hover originated from (panel or canvas)
export type HoveredPinSource = "panel" | "canvas" | null;
export const hoveredPinSourceAtom = atom<HoveredPinSource>(null);

// Maximum number of pins allowed per session
export const MAX_PINS = 5;

// Colors for pin markers
export const PIN_COLORS = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#F59E0B", // Amber
  "#14B8A6" // Teal
];

// Mobile view state for pinning panel
export type PinningMobileView = "list" | "detail";
export const pinningMobileViewAtom = atom<PinningMobileView>("list");

// Mobile panel expanded state
export const pinningPanelExpandedAtom = atom<boolean>(false);

// Tracks pinning availability per artwork
export const pinningEnabledArtworksAtom = atom<Record<string, boolean>>({});
